import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState, useRef, Suspense } from "react";
import Button from "../Button";
import ClearIcon from "@mui/icons-material/Clear";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import TextInput from "../TextInput";
import classNames from "classnames";
import loadable from "@loadable/component";
import { useStyles } from "./styles";
const SearchSuggestionInput = loadable(() => import("./SearchSuggestionInput"));
const SearchInputFilters = loadable(() => import("./SearchInputFilter"));
export const SearchInput = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    useHotkey,
    className,
    style,
    onSearch,
    onSearchSuggestion,
    initialWidth = 100,
    maxWidth = "100%",
    onChange,
    suggestions,
    maxSuggestions = 6,
    renderSuggestion,
    mustSelectSuggestion,
    onClear,
    searchOnChange,
    placeholder = "Search...",
    AutosuggestProps,
    variant,
    value,
    filters,
    activeFilters,
    onFilterReset,
    autoFocus,
    portalElement,
    disableSuggestionFilters,
    widgetId,
    numberOfCharactersForSuggestions,
    ...otherProps
  } = props;
  const classes = useStyles({
    initialWidth,
    maxWidth,
    ...props
  });
  const [focused, setFocused] = useState(false);
  const inputRef = useRef();
  // Only used when not controlled, otherwise use onChange and value
  const [uncontrolledValue, setUncontrolledValue] = useState("");

  // useEffect(() => {
  // 	if (otherProps.value !== undefined && value !== otherProps.value) setUncontrolledValue(otherProps.value);
  // }, [otherProps.value, value]);

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);
  const handleClear = () => {
    setUncontrolledValue("");
    if (typeof onChange === "function") {
      onChange({
        target: {
          value: ""
        }
      });
    }
    if (typeof onClear === "function") onClear();
  };
  const handleSearch = () => {
    if (typeof onSearch === "function") {
      inputRef.current?.blur();
      setTimeout(() => handleBlur(), 100); // Small delay added to ensure animation fired
      onSearch(value !== undefined ? value : uncontrolledValue);
    }
  };
  const handleSearchKeyDown = targetValue => {
    if (typeof onSearch === "function") {
      inputRef.current?.blur();
      setTimeout(() => handleBlur(), 100); // Small delay added to ensure animation fired
      onSearch(targetValue);
    }
  };
  const handleChange = event => {
    setUncontrolledValue(event.target.value);
    if (typeof onChange === "function") {
      onChange(event);
    }
  };
  useEffect(() => {
    let listener;
    if (inputRef.current && useHotkey) {
      listener = event => {
        if ((event.ctrlKey || event.metaKey) && event.key === "k") {
          event.preventDefault();
          inputRef.current?.focus();
          inputRef.current?.select();
        }
      };
      document.addEventListener("keydown", listener);
    }
    return () => {
      if (inputRef && useHotkey) {
        document.removeEventListener("keydown", listener);
      }
    };
  }, [inputRef, useHotkey]);
  const handleKeyDown = event => {
    // Handle enter press on input
    if (event.key === "Enter") {
      const target = event.target;
      const targetValue = target.value;
      handleSearchKeyDown(targetValue);
    } else if (event.key === "Escape") {
      inputRef.current?.blur();
      handleBlur();
    }
  };
  const renderedTextInputProps = {
    ...otherProps,
    className: classNames(className, classes.root, {
      [classes.focused]: focused,
      [classes.filterButtonPadding]: filters
    }),
    value: value !== undefined ? value : uncontrolledValue,
    variant,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    placeholder: placeholder,
    autoFocus: autoFocus,
    inputProps: {
      "aria-label": "label" in otherProps ? undefined : placeholder,
      ref: inputRef,
      ...otherProps.inputProps,
      autoFocus: autoFocus
    },
    endAdornment: (onChange || mustSelectSuggestion) && searchOnChange ? value === "" || value === undefined && uncontrolledValue === "" ? /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: Boolean(filters)
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      "aria-label": "Search",
      disabled: true
    }, /*#__PURE__*/React.createElement(SearchIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, _extends({}, props, {
      filters: filters
    }))) : /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: Boolean(filters)
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      title: "Clear search",
      onClick: handleClear
    }, /*#__PURE__*/React.createElement(ClearIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, _extends({}, props, {
      filters: filters
    }))) : value === "" || value === undefined && uncontrolledValue === "" ? /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: Boolean(filters)
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      "aria-label": "Search",
      disabled: true
    }, /*#__PURE__*/React.createElement(SearchIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, _extends({}, props, {
      filters: filters
    }))) : /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end",
      className: classNames({
        [classes.withSearch]: Boolean(filters)
      })
    }, /*#__PURE__*/React.createElement(Button, {
      type: "icon",
      size: variant === "compact" ? "small" : undefined,
      title: "Search",
      onClick: handleSearch
    }, /*#__PURE__*/React.createElement(SearchIcon, {
      fontSize: "small"
    })), filters && filters.length > 0 && /*#__PURE__*/React.createElement(SearchInputFilters, _extends({}, props, {
      filters: filters
    })))
  };
  const textInput = /*#__PURE__*/React.createElement(TextInput, _extends({
    ref: ref
  }, renderedTextInputProps, {
    onChange: e => {
      setUncontrolledValue(e.target.value);
      if (typeof onChange === "function") onChange(e);
    }
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: "prmbl-searchInput " + classes.outerWrapper,
    style: style
  }, suggestions ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: textInput
  }, /*#__PURE__*/React.createElement(SearchSuggestionInput, _extends({
    ref: ref
  }, props, {
    maxSuggestions: maxSuggestions,
    "aria-label": placeholder,
    TextInputProps: {
      ...renderedTextInputProps,
      onChange: handleChange
    }
  }))) : textInput);
});
SearchInput.displayName = "SearchInput";
SearchInput.defaultProps = {
  placeholder: "Search...",
  initialWidth: 100,
  maxWidth: "100%",
  maxSuggestions: 6
};
export default SearchInput;